import { useState, useEffect } from 'react';
import { useCart } from '../cart/CartManager';
import { useTranslation } from 'react-i18next';
import { formatNumberMax1Digit } from '../utils/numberUtils';
import { getProductName, getMarketName, getBrandName } from '../utils/productUtils';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { calculateCartPrice, getOrderSummary } from '../api/shop';
import { useAuth } from '../auth';
import ShippingMethodDialog from '../components/products/ShippingMethodDialog';
import { useNavigate } from 'react-router-dom';
import { CartCalculation } from '../types/shop';
import { textStyles } from '../styles/textstyle';
import DiscountDetails from '../components/shop/DiscountDetails';
import ProductDiscount from '../components/shop/ProductDiscount';
import { usePromoCodes } from '../hooks/usePromoCodes';
import PromoOffer from '../components/shop/PromoOffer';
import { useMarkets } from '../api/product';
import type { Market } from '../api/product';
import { useWishlist } from '../contexts/WishlistContext';

const MARKET_ID_KEY = 'selected_market_id';

const Cart = () => {
  const { t, i18n } = useTranslation();
  const { cartItems, updateQuantity, removeFromCart, clearCart, updateCartFromAPIResponse } = useCart();
  const { isAuthenticated } = useAuth();
  const { addToWishlist, isInWishlist } = useWishlist();
  const [cartCalculation, setCartCalculation] =
    useState<CartCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [quantityLoading, setQuantityLoading] = useState<Set<number>>(new Set());
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [shippingMethodDialogOpen, setShippingMethodDialogOpen] =
    useState(false);
  const [selectedMarket, setSelectedMarket] = useState<{
    marketId: number;
    name: string;
    market?: Market;
  } | null>(null);
  const navigate = useNavigate();
  
  // Get markets data to find Hong Kong market
  const { marketsData } = useMarkets();

  // Promo code management
  const {
    newPromoCode,
    setNewPromoCode,
    isApplying,
    removingCode,
    error,
    successMessage,
    applyPromoCode,
    removePromoCode,
    clearPromoCodes,
  } = usePromoCodes({
    cartCalculation,
    onCartUpdate: setCartCalculation,
    isAuthenticated,
  });

  // Handle adding product to wishlist
  const handleAddToWishlist = async (productId: number) => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/sign-in');
      return;
    }
    
    try {
      await addToWishlist(productId);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };

  // Get marketId from sessionStorage (set by ProductDetails)
  const getMarketId = () => sessionStorage.getItem(MARKET_ID_KEY) || '';

  // Helper function to validate stock before API calls - same logic as ProductDetails
  const validateStockBeforeAPI = (items: Array<{productId: number; quantity: number; variantId?: number}>, cartItems: Array<{id: number; variantId?: number; name?: string; inStock?: boolean; maxAvailable?: number; inventory?: {available: number}}>): boolean => {
    console.log('Cart: validateStockBeforeAPI called with items:', items);
    console.log('Cart: validateStockBeforeAPI called with cartItems:', cartItems);
    
    for (const item of items) {
      const cartItem = cartItems.find(ci => ci.id === item.productId && ci.variantId === item.variantId);
      if (cartItem) {
        console.log('Cart: Validating item:', cartItem.name, 'requested quantity:', item.quantity);
        console.log('Cart: Cart item details:', {
          inStock: cartItem.inStock,
          maxAvailable: cartItem.maxAvailable,
          inventoryAvailable: cartItem.inventory?.available
        });
        
        const inStock = cartItem.inStock;
        const maxAvailable = cartItem.maxAvailable;
        const available = cartItem.inventory?.available;
        
        // First check if product is out of stock
        if (inStock === false) {
          console.error('Cart: Product is out of stock:', cartItem.name || 'Unknown product');
          alert(t('cart.error.alertOutOfStock').replace('{{productName}}', cartItem.name || 'Unknown product'));
          return false;
        }
        
        // Then check if quantity exceeds available stock
        const effectiveMaxAvailable = maxAvailable !== undefined ? maxAvailable : available;
        console.log('Cart: Effective max available:', effectiveMaxAvailable, 'vs requested:', item.quantity);
        
        if (effectiveMaxAvailable !== undefined && effectiveMaxAvailable !== null && item.quantity > effectiveMaxAvailable) {
          console.error('Cart: Quantity exceeds available stock:', cartItem.name || 'Unknown product', 'requested:', item.quantity, 'available:', effectiveMaxAvailable);
          alert(t('cart.error.alertLimitedStock').replace('{{productName}}', cartItem.name || 'Unknown product').replace('{{count}}', effectiveMaxAvailable.toString()));
          return false;
        }
      } else {
        console.warn('Cart: Could not find cart item for validation:', item.productId, item.variantId);
      }
    }
    console.log('Cart: Stock validation passed');
    return true;
  };

  // Initialize default market if none is set
  useEffect(() => {
    if (!marketsData || marketsData.length === 0) return;
    
    const currentMarketId = getMarketId();
    if (!currentMarketId) {
      // Find Hong Kong market from API data
      const hongKongMarket = marketsData.find((market: Market) => 
        market.countriesWithNames.some(country => country.code === 'HK')
      );
      
      if (hongKongMarket) {
        sessionStorage.setItem(MARKET_ID_KEY, hongKongMarket.id.toString());
        setSelectedMarket({ marketId: hongKongMarket.id, name: hongKongMarket.name, market: hongKongMarket });
      }
    } else {
      // Load existing market from sessionStorage
      const marketId = parseInt(currentMarketId);
      const existingMarket = marketsData.find((market: Market) => market.id === marketId);
      setSelectedMarket({ 
        marketId, 
        name: existingMarket?.name || 'Hong Kong',
        market: existingMarket
      });
    }
  }, [marketsData]);

  // Call appropriate API on mount based on authentication status
  useEffect(() => {
    const fetchCart = async () => {
      // Wait for markets data to be loaded before making API calls
      if (!marketsData || marketsData.length === 0) return;
      
      setLoading(true);
      try {
        const marketId = getMarketId();
        
        // Ensure we have a valid market ID before making API calls
        if (!marketId) {
          console.log('No market ID found, waiting for market initialization');
          return;
        }
        
        if (isAuthenticated) {
          // For authenticated users, use order-summary API
          const result = await getOrderSummary({
            cartItems: [], // Always empty for authenticated users
            shippingMethodId: null,
            marketId: marketId,
            promoCodes: [],
          });
          setCartCalculation(result);
        } else {
          // For guest users, use calculate-price API with local cart items
          const items = cartItems.map(
            (item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
              productId: item.id,
              quantity: item.quantity,
              variantId: item.variantId,
              selectedOptions: item.selectedOptions,
            })
          );
          if (!items.length) {
            setCartCalculation(null);
            return;
          }
          
          // Validate stock before calling API
          if (!validateStockBeforeAPI(items, cartItems)) {
            setCartCalculation(null);
            return;
          }
          
          const result = await calculateCartPrice({
            cartItems: items,
            shippingMethodId: null,
            marketId: marketId,
            promoCodes: [],
          });
          setCartCalculation(result);
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
        setCartCalculation(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCart();
  }, [isAuthenticated, marketsData]); // Add marketsData as dependency

  // Remove the duplicate useEffect that was causing double API calls

  // Watch for changes in cart items and recalculate cart (only for guest users)
  useEffect(() => {
    const recalculateCart = async () => {
      if (isAuthenticated) return; // Only for guest users
      
      const marketId = getMarketId();
      if (!marketId) return; // Wait for market ID to be available
      
      try {
        // Only for guest users, use calculate-price API with local cart items
        const localCart = JSON.parse(
          sessionStorage.getItem('cart_items') || '[]'
        );
        const items = localCart.map(
          (item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
            productId: item.id,
            quantity: item.quantity,
            variantId: item.variantId,
            selectedOptions: item.selectedOptions,
          })
        );
        if (!items.length) {
          setCartCalculation(null);
          return;
        }
        const result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: null,
          marketId: marketId,
          promoCodes: [],
        });
        setCartCalculation(result);
      } catch (error) {
        console.error('Error recalculating cart:', error);
      }
    };

    // Only trigger recalculation when cartCalculation is null and we have market data
    if (cartCalculation === null && marketsData && marketsData.length > 0) {
      const timeoutId = setTimeout(recalculateCart, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [cartCalculation === null, isAuthenticated, marketsData]); // Add marketsData as dependency

  // Refresh cart calculation when component mounts or when user navigates back to cart
  useEffect(() => {
    const refreshCart = async () => {
      const marketId = getMarketId();
      if (!marketId || !marketsData || marketsData.length === 0) return;
      
      try {
        if (isAuthenticated) {
          // For authenticated users, use order-summary API
          const result = await getOrderSummary({
            cartItems: [], // Always empty for authenticated users
            shippingMethodId: null,
            marketId: marketId,
            promoCodes: [],
          });
          setCartCalculation(result);
        } else {
          // For guest users, use calculate-price API with local cart items
          const items = cartItems.map(
            (item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
              productId: item.id,
              quantity: item.quantity,
              variantId: item.variantId,
              selectedOptions: item.selectedOptions,
            })
          );
          if (!items.length) {
            setCartCalculation(null);
            return;
          }
          
          // Validate stock before calling API
          if (!validateStockBeforeAPI(items, cartItems)) {
            setCartCalculation(null);
            return;
          }
          
          const result = await calculateCartPrice({
            cartItems: items,
            shippingMethodId: null,
            marketId: marketId,
            promoCodes: [],
          });
          setCartCalculation(result);
        }
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    };

    // Refresh cart when component mounts or when market data is available
    if (marketsData && marketsData.length > 0) {
      refreshCart();
    }
  }, [marketsData, isAuthenticated]); // Refresh when market data changes or authentication status changes

  const handleUpdateQuantity = async (cartId: number, quantity: number) => {
    console.log('Cart: Updating quantity for cart item', cartId, 'to', quantity);
    
    // Validate quantity before updating
    if (quantity <= 0) {
      console.error('Cart: Invalid quantity:', quantity);
      return;
    }
    
    // Set loading state for this specific item
    setQuantityLoading(prev => new Set(prev).add(cartId));
    
    try {
      // Update quantity in CartManager
      const success = await updateQuantity(cartId, quantity);
      console.log('Cart: updateQuantity success:', success);
      
      if (success) {
        console.log('Cart: updateQuantity was successful, proceeding with cart calculation refresh');
        // Refresh cart calculation with updated data
        if (isAuthenticated) {
          const marketId = getMarketId();
          console.log('Cart: Authenticated user - cartItems:', cartItems);
          console.log('Cart: Authenticated user - cartId:', cartId, 'quantity:', quantity);
          
          // For authenticated users, also send updated cart items
          const items = cartItems.map(
            (item: { id: number; cartId?: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
              productId: item.id,
              quantity: item.cartId === cartId ? quantity : item.quantity, // Use updated quantity for the specific item
              variantId: item.variantId,
              selectedOptions: item.selectedOptions,
            })
          );
          
          console.log('Cart: Authenticated user - mapped items:', items);
          
          // Add a small delay to ensure server has processed the update
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const result = await getOrderSummary({
            cartItems: [], // Use empty array to get server's current state
            shippingMethodId: null,
            marketId: marketId,
            promoCodes: [],
          });
          
          console.log('Cart: getOrderSummary API response:', result);
          console.log('Cart: getOrderSummary items:', result?.items);
          
          if (result) {
            // Update CartManager with the API response
            updateCartFromAPIResponse(result);
            setCartCalculation(result);
            console.log('Cart: Updated cartCalculation with result:', result);
          }
        } else {
          // For guest users, recalculate with updated cartItems
          const marketId = getMarketId();
          if (marketId) {
            // Find the updated item and use the new quantity
            const items = cartItems.map(
              (item: { id: number; cartId?: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
                productId: item.id,
                quantity: item.cartId === cartId ? quantity : item.quantity, // Use updated quantity for the specific item
                variantId: item.variantId,
                selectedOptions: item.selectedOptions,
              })
            );
            
            if (items.length > 0) {
              console.log('Cart: Guest user - calling calculateCartPrice with items:', items);
              
              // Validate stock before calling API
              // Create updated cartItems with the new quantity for validation
              const updatedCartItemsForValidation = cartItems.map(item => 
                item.cartId === cartId ? { ...item, quantity } : item
              );
              
              if (!validateStockBeforeAPI(items, updatedCartItemsForValidation)) {
                return;
              }
              
              const result = await calculateCartPrice({
                cartItems: items,
                shippingMethodId: null,
                marketId: marketId,
                promoCodes: [],
              });
              
              console.log('Cart: Guest user - calculateCartPrice response:', result);
              
              if (result) {
                // Check if the API response indicates inventory issues
                if (result.error && (result.error.includes('inventory') || result.error.includes('stock') || result.error.includes('available'))) {
                  console.error('Cart: Inventory error from calculateCartPrice:', result.error);
                  // Show error to user and don't update the cart
                  alert(`Inventory Error: ${result.error}`);
                  return;
                }
                
                // Check if any items in the response indicate inventory issues
                if (result.items && result.items.some((item: {maxAvailable?: number; quantity: number}) => item.maxAvailable !== undefined && item.quantity > item.maxAvailable)) {
                  console.error('Cart: One or more items exceed available inventory');
                  alert('Some items exceed available inventory. Please reduce quantities.');
                  return;
                }
                
                // Update CartManager with the API response
                updateCartFromAPIResponse(result);
                setCartCalculation(result);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      // Clear loading state for this item
      setQuantityLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartId);
        return newSet;
      });
    }
  };

  const handleRemove = async (cartId: number) => {
    try {
      // Remove item from cart
      await removeFromCart(cartId);
      
      // For authenticated users, directly refresh cart data
      if (isAuthenticated) {
        const marketId = getMarketId();
        const result = await getOrderSummary({
          cartItems: [],
          shippingMethodId: null,
          marketId: marketId,
          promoCodes: [],
        });
        setCartCalculation(result);
      } else {
        // For guest users, recalculate directly from current cart items
        const marketId = getMarketId();
        if (marketId) {
          try {
            // Get updated cart items from context (already updated by removeFromCart)
            const updatedCartItems = cartItems.filter(item => item.cartId !== cartId);
            
            if (updatedCartItems.length === 0) {
              setCartCalculation(null);
              return;
            }
            
            const items = updatedCartItems.map(item => ({
              productId: item.id,
              quantity: item.quantity,
              variantId: item.variantId,
              selectedOptions: item.selectedOptions,
            }));
            
            const result = await calculateCartPrice({
              cartItems: items,
              shippingMethodId: null,
              marketId: marketId,
              promoCodes: [],
            });
            setCartCalculation(result);
          } catch (error) {
            console.error('Error recalculating cart after removal:', error);
            // Fallback to triggering recalculation
            setCartCalculation(null);
          }
        }
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      
      // For authenticated users, directly refresh cart data
      if (isAuthenticated) {
        const marketId = getMarketId();
        const result = await getOrderSummary({
          cartItems: [],
          shippingMethodId: null,
          marketId: marketId,
          promoCodes: [],
        });
        setCartCalculation(result);
      } else {
        // For guest users, clear cart calculation to trigger recalculation
        setCartCalculation(null);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const handleImageError = (itemId: number) => {
    setImageErrors(prev => new Set(prev).add(itemId));
  };

  const handleSelectMarket = async (marketId: number, name: string) => {
    const market = marketsData.find((m: Market) => m.id === marketId);
    setSelectedMarket({ marketId, name, market });
    // Update sessionStorage with the new market ID
    sessionStorage.setItem(MARKET_ID_KEY, marketId.toString());
    
    // Immediately refresh cart calculation when market changes
    try {
      if (isAuthenticated) {
        // For authenticated users, use order-summary API
        const result = await getOrderSummary({
          cartItems: [], // Always empty for authenticated users
          shippingMethodId: null,
          marketId: marketId.toString(),
          promoCodes: [],
        });
        setCartCalculation(result);
      } else {
        // For guest users, use calculate-price API with local cart items
        const localCart = JSON.parse(
          sessionStorage.getItem('cart_items') || '[]'
        );
        const items = localCart.map(
          (item: { id: number; quantity: number; variantId?: number; selectedOptions?: { [key: string]: string } }) => ({
            productId: item.id,
            quantity: item.quantity,
            variantId: item.variantId,
            selectedOptions: item.selectedOptions,
          })
        );
        if (!items.length) {
          setCartCalculation(null);
          return;
        }
        
        // Validate stock before calling API
        if (!validateStockBeforeAPI(items, localCart)) {
          setCartCalculation(null);
          return;
        }
        
        const result = await calculateCartPrice({
          cartItems: items,
          shippingMethodId: null,
          marketId: marketId.toString(),
          promoCodes: [],
        });
        setCartCalculation(result);
      }
    } catch (error) {
      console.error('Error refreshing cart after market change:', error);
      // Fallback to null to trigger the useEffect refresh
      setCartCalculation(null);
    }
  };

  const handleProceedToCheckout = () => {
    if (cartCalculation && cartCalculation.items?.length) {
      if (!isAuthenticated) {
        // Redirect to login page, preserving the cart page as the intended destination
        navigate('/sign-in', { state: { from: { pathname: '/cart' } } });
        return;
      }
      navigate('/checkout', { state: { cartCalculation } });
    }
  };

  const subtotal = cartCalculation?.breakdown?.subtotal || 0;
  const currency = cartCalculation?.currency || 'HK$';
  
  // Use new breakdown structure from API response
  const totalItemsValue = cartCalculation?.breakdown?.totalItemsValue || 0;
  const totalDiscount = cartCalculation?.breakdown?.totalDiscount || 0;
  const finalTotal = cartCalculation?.breakdown?.finalTotal || 0;
  
  // Fallback to display structure for backward compatibility
  const display = cartCalculation?.display || {
    totalItemsValue: '',
    productDiscount: '',
    promoDiscount: null,
    buyXGetYDiscount: null,
    totalDiscount: '',
    subtotal: '',
    shippingCost: '',
    taxAmount: null,
    finalTotal: ''
  };
  const shipping = cartCalculation?.shipping;

  // Remove frontend calculation - rely only on API data
  // The API should provide all shipping information including free shipping qualification
  const calculatedShipping = shipping;

  return (
    <Grid container spacing={4} sx={{ maxWidth: { xs: '100%', md: 'lg' }, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Left Column */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: { xs: 1, md: 1.5 },
            backgroundColor: '#FCFAF6',
            border: '1px solid #E1DACA',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <img
                src="/icons/ic_shipping.svg"
                alt="Shipping"
                style={{ width: 20, height: 20 }}
              />
              <Typography fontWeight={500}>
                {t('cart.shippingTo')} {selectedMarket?.market ? getMarketName(selectedMarket.market, i18n.language) : t('cart.hongKong')}
              </Typography>
            </Box>
            <Button
              sx={{
                color: '#17110D',
                textDecoration: 'underline',
                fontSize: '14px',
              }}
              variant="text"
              size="small"
              onClick={() => setShippingMethodDialogOpen(true)}
            >
              {t('cart.change')}
            </Button>
          </Box>
        </Paper>
        {calculatedShipping?.qualifiesForFreeShipping && (
          <Box mb={1}>
            <Typography color="#17110D" fontWeight={400} fontSize={14}>
                                {t('cart.qualifiesForFreeShipping')}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{ height: 6, borderRadius: 3, mt: 1 }}
              color="success"
            />
          </Box>
        )}
        {!calculatedShipping?.qualifiesForFreeShipping &&
          calculatedShipping?.freeShippingProgress !== undefined && (
            <Box mb={1}>
              <Typography color="text.secondary" fontWeight={500}>
                {calculatedShipping?.amountForFreeShipping
                                    ? t('cart.spendMoreForFreeShipping').replace('{amount}', `${currency}${formatNumberMax1Digit(calculatedShipping.amountForFreeShipping)}`)
                    : t('cart.freeShippingProgress')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calculatedShipping.freeShippingProgress * 100}
                sx={{ height: 6, borderRadius: 3, mt: 1 }}
                color="success"
              />
            </Box>
          )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography fontWeight={500} fontSize={14}>
            {cartCalculation?.items?.length || 0} items
          </Typography>
          {cartCalculation?.items?.length && (
            <Button
              variant="text"
              color="secondary"
              onClick={handleClearCart}
              disabled={loading}
            >
              {t('cart.removeAll')}
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          Array.from({ length: 2 }).map((_, idx) => (
            <Box
              key={idx}
              mb={2}
              p={2}
              borderRadius="4px"
              border="1px solid #E1DACA"
              sx={{ background: '#fff' }}
            >
              <Box display="flex" alignItems="flex-start" mb={1}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={80}
                  sx={{ borderRadius: 2, marginRight: 8 }}
                />
                <Box flex={1} ml={1}>
                  <Skeleton width="60%" height={24} />
                  <Skeleton width="80%" height={18} />
                </Box>
              </Box>
              <Skeleton width="40%" height={32} />
            </Box>
          ))
        ) : cartCalculation?.items?.length === 0 || !cartCalculation?.items ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            width="100%"
            py={6}
          >
            <img
              src="/icons/ic_cart_empty.svg"
              alt={t('cart.emptyCart')}
              style={{ width: 68, height: 68 }}
            />
            <Typography
              sx={{ color: '#85898C', fontWeight: 400, fontSize: 14, mt: 2 }}
            >
              {t('cart.cartEmptyMessage')}
            </Typography>
            <Typography
              sx={{
                color: '#85898C',
                fontWeight: 400,
                fontSize: 14,
                textAlign: 'center',
                mb: 4,
              }}
              dangerouslySetInnerHTML={{
                __html: t('cart.cartEmptyDescription').replace(
                  'You Might Also Like',
                  `<span style="font-weight: 700">${t('cart.youMightAlsoLike')}</span>`
                )
              }}
            />
            <Button
              variant="outlined"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: '#17110D',
                borderColor: '#17110D',
                borderRadius: 2,
                px: 6,
                py: 1,
                mt: 2,
                width: 400,
                maxWidth: '90%',
              }}
              onClick={() => (window.location.href = '/')}
            >
              {t('cart.continueShopping')}
            </Button>
          </Box>
        ) : (
          <>
            {cartCalculation.items.map(item => {
              // Find the corresponding cartId from CartManager
              const cartManagerItem = cartItems.find(ci => 
                ci.id === item.productId && 
                ci.variantId === item.variantId &&
                JSON.stringify(ci.selectedOptions || null) === JSON.stringify(item.selectedOptions || null)
              );
              const actualCartId = cartManagerItem?.cartId || item.cartId || item.id;
              
              console.log('Cart: Rendering cartCalculation item:', item);
              console.log('Cart: Found cartManagerItem:', cartManagerItem);
              console.log('Cart: Using actualCartId:', actualCartId);
              
              return (
              <Box
                key={item.id}
                display="flex"
                flexDirection="column"
                mb={2}
                p={{ xs: 1.5, md: 2 }}
                borderRadius={'4px'}
                border={'1px solid #E1DACA'}
                sx={{ background: '#fff' }}
              >
                {/* Top Row: Image + Title/Description */}
                <Box display="flex" alignItems="flex-start" mb={1}>
                  {imageErrors.has(item.id) ? (
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        marginRight: 8,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'grey.500',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption">{t('cart.noImage')}</Typography>
                    </Box>
                  ) : (
                    <img
                      src={item.product.imageUrl}
                      alt={item.product.name}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 2,
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                      onError={() => handleImageError(item.productId)}
                    />
                  )}
                  <Box flex={1} ml={1}>
                    <Typography 
                      sx={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        mb: 0.5 
                      }}
                    >
                      {getBrandName(item.product, i18n.language)}
                    </Typography>
                    
                    {/* Selected Variants */}
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                        {Object.entries(item.selectedOptions).map(([optionKey, optionValue]) => (
                          <Chip
                            key={optionKey}
                            label={`${optionKey}: ${optionValue}`}
                            size="small"
                            sx={{
                              backgroundColor: '#F5F5F5',
                              color: '#666',
                              fontSize: '12px',
                              height: '20px',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      mb={0.5}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.4,
                        wordWrap: 'break-word',
                        '& img': {
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          margin: '4px 0',
                        },
                        '& p': {
                          margin: '4px 0',
                        },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          margin: '6px 0 4px 0',
                          fontWeight: 400,
                          fontSize: '14px',
                        },
                        '& ul, & ol': {
                          margin: '4px 0',
                          paddingLeft: '16px',
                        },
                        '& li': {
                          margin: '2px 0',
                        },
                      }}
                    >{getProductName(item.product, i18n.language)}</Typography>
                  </Box>
                  <Box ml={1} mt={0.5}>
                    <IconButton 
                      size="small"
                      onClick={() => handleAddToWishlist(item.productId)}
                      sx={{ 
                        color: isInWishlist(item.productId) ? '#b48b6a' : '#131518',
                        '&:hover': { color: '#b48b6a' }
                      }}
                    >
                      {isInWishlist(item.productId) ? (
                        <FavoriteIcon style={{ fontSize: 20, color: '#b48b6a' }} />
                      ) : (
                        <FavoriteBorderIcon style={{ fontSize: 20, color: '#131518' }} />
                      )}
                    </IconButton>
                  </Box>
                </Box>
                {/* Middle Row: Quantity + Price/Discount */}
                <Box
                  display="flex"
                  flexDirection={'row'}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  mb={1}
                  gap={{ xs: 1, sm: 0 }}
                >
                  {/* Quantity control */}
                  <Box
                    display="flex"
                    alignItems="center"
                    border={1}
                    borderColor="#E1DACA"
                    borderRadius={1}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        handleUpdateQuantity(
                          actualCartId,
                          Math.max(1, item.quantity - 1)
                        )
                      }
                      disabled={item.quantity === 1 || loading}
                      sx={{
                        minWidth: 24,
                        p: 0,
                        borderRight: '1px solid #E1DACA',
                        borderRadius: '0px',
                      }}
                    >
                      -
                    </Button>
                    <Typography mx={'12px'}>{item.quantity}</Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        handleUpdateQuantity(actualCartId, item.quantity + 1)
                      }
                      disabled={loading}
                      sx={{
                        minWidth: 24,
                        p: 0,
                        borderLeft: '1px solid #E1DACA',
                        borderRadius: '0px',
                      }}
                    >
                      +
                    </Button>
                  </Box>
                  {/* Price/Discount */}
                  {<Box
                    textAlign={{ xs: 'left', sm: 'right' }}
                    display="flex"
                    flexDirection="column"
                    alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                    gap={0.5}
                  >
                    {!quantityLoading.has(item.cartId) && item.totalComparePrice > item.unitPrice && item.savings > 0 && (
                      <>
                        <Typography sx={textStyles.priceSmall}>
                          {currency}
                          {item.totalComparePrice.toFixed(2)}
                        </Typography>
                        <Typography sx={textStyles.savingsText}>
                          Save {currency}{item.savings.toFixed(2)}
                        </Typography>
                      </>
                    )}
                    {!quantityLoading.has(item.cartId) && (
                      <Typography sx={{ ...textStyles.priceLarge, fontSize: '16px' }}>
                        {currency}
                        {item.totalPrice?.toFixed(2)}
                      </Typography>
                    )}
                  </Box>}
                </Box>
                {/* Product-specific Discounts */}
                {!quantityLoading.has(item.cartId) && (
                  <ProductDiscount
                    appliedDiscounts={cartCalculation?.validatedDiscounts?.appliedDiscounts || []}
                    currency={currency}
                  />
                )}
                
                {/* Bottom Row: Save for later + Remove */}
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="flex-end"
                  mt={1}
                  gap={{ xs: 1, sm: 0 }}
                >
                  <Button
                    variant="text"
                    color="inherit"
                    sx={{
                      p: 0,
                      minWidth: 0,
                      ...textStyles.buttonText,
                      color: '#757d86',
                    }}
                    onClick={() => handleRemove(actualCartId)}
                    disabled={loading}
                  >
                    {t('cart.remove')}
                  </Button>
                </Box>
              </Box>
              );
            })}
          </>
        )}
      </Grid>
      {/* Right Column */}
      <Grid size={{ xs: 12, md: 6 }}>
        <PromoOffer
          display={display}
          newPromoCode={newPromoCode}
          setNewPromoCode={setNewPromoCode}
          isApplying={isApplying}
          removingCode={removingCode}
          error={error}
          successMessage={successMessage}
          onApplyPromoCode={applyPromoCode}
          onRemovePromoCode={removePromoCode}
          onClearPromoCodes={clearPromoCodes}
          ignoredDiscounts={cartCalculation?.validatedDiscounts?.ignoredDiscounts || []}
          appliedDiscounts={cartCalculation?.validatedDiscounts?.appliedDiscounts || []}
        />
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: '#F2EFEA',
            border: '1px solid #E1DACA',
            borderRadius: '4px',
          }}
        >
          <Typography variant="h6" sx={textStyles.h6} mb={2}>
            {t('cart.orderPreview.title')}
          </Typography>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography sx={textStyles.orderSummaryLabel}>{t('cart.subtotal')}</Typography>
            <Typography sx={textStyles.orderSummaryLabel}>
              {display.subtotal || `${currency}${subtotal.toFixed(2)}`}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography sx={textStyles.orderSummaryValue}>
              {t('checkout.totalItemsValue')}
            </Typography>
            <Typography sx={textStyles.orderSummaryValue}>
              {currency}{totalItemsValue.toFixed(2)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography sx={textStyles.orderSummaryValue}>
              {t('checkout.totalDiscount')}
            </Typography>
            <Typography sx={textStyles.orderSummaryValue}>
              {currency}{totalDiscount.toFixed(2)}
            </Typography>
          </Box>
          
          {/* Discount Details */}
          {((cartCalculation?.validatedDiscounts?.appliedDiscounts && cartCalculation.validatedDiscounts.appliedDiscounts.length > 0) || (display.productDiscount && display.productDiscount !== '-HK$0.00')) && (
            <DiscountDetails
              appliedDiscounts={cartCalculation?.validatedDiscounts?.appliedDiscounts || []}
              currency={currency}
              display={display}
              cartItems={cartCalculation?.items || []}
            />
          )}
          <Box display="flex" justifyContent="space-between" mb={1} mt={2}>
            <Typography sx={textStyles.orderSummaryValue}>
              {t('cart.shipping')}
            </Typography>
            <Typography sx={textStyles.orderSummaryTax}>
                              {t('cart.calculatedInCheckout')}
            </Typography>
          </Box>
          <Divider sx={{ my: 2, color: '#ECECEC' }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography sx={textStyles.orderSummaryTotal}>
              {t('checkout.orderTotal')} <br></br>{' '}
              <span style={textStyles.orderSummaryTotalSub}>
                (
                <span style={textStyles.orderSummaryTotalSubBold}>
                  {cartCalculation?.items?.length}
                </span>
                {t('cart.items')})
              </span>
            </Typography>
            <Typography sx={textStyles.orderSummaryTotal}>
              {currency}{finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="inherit"
            size="large"
            fullWidth
            sx={{
              bgcolor: '#b48b6a',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
            }}
            disabled={loading || !cartCalculation?.items?.length}
            onClick={handleProceedToCheckout}
          >
            {t('cart.proceedToCheckout')}
          </Button>
          <Typography sx={textStyles.orderSummaryNote} mt={2}>
            {t('cart.paymentNote')}
          </Typography>
          <Box display="flex" gap={2} mt={2}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Box
                key={i}
                width={48}
                height={24}
                bgcolor="#eee"
                borderRadius={2}
              />
            ))}
          </Box>
        </Paper>
      </Grid>
      <ShippingMethodDialog
        open={shippingMethodDialogOpen}
        onClose={() => setShippingMethodDialogOpen(false)}
        selectedMarket={selectedMarket}
        onSelectMarket={handleSelectMarket}
      />
    </Grid>
  );
};

export default Cart;
